import React, { FunctionComponent, useState, useEffect } from 'react';
import { isNil } from 'ramda';
import AutoSizer from 'react-virtualized-auto-sizer';
import InfiniteLoader from 'react-window-infinite-loader';
import { VariableSizeList as List, ListChildComponentProps } from 'react-window';
import { IBindingCallback1, IBindingCallback2, IBindingFunction } from '../../common/models/callback';
import LoaderWrapper from '../LoaderWrapper';
import NoDataPlaceholder from '../NoDataPlaceholder';
import styles from './styles.module.scss';
import { concat } from '../../common/helpers/arrayHelper';

interface IProps {
  items: JSX.Element[];
  total?: number;
  selectOnClick?: boolean;
  loading?: boolean;
  footer?: JSX.Element;
  footerHeight?: number;
  getItemHeight?: IBindingFunction<number, number>;
  onSelect: IBindingCallback1<number>;
  loadMore?: IBindingCallback2<number, number>;
}

const KeyNavigationList: FunctionComponent<IProps> = ({
  items,
  total = items.length,
  loading,
  footer,
  getItemHeight = (index: number) => 50,
  footerHeight = 55,
  selectOnClick,
  onSelect,
  loadMore = () => null
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  let listRef: List;

  const keyHandler = (event: KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        const index = isNil(selectedIndex) ? -1 : selectedIndex;
        const next = event.key === 'ArrowUp' ? index - 1 : index + 1;
        if (items[next]) {
          event.preventDefault();
          listRef.scrollToItem(next, 'smart');
          setSelectedIndex(next);
        }
        break;
      case 'Enter':
        if (!isNil(selectedIndex)) {
          setSelectedIndex(null);
          onSelect(selectedIndex);
        }
      // no default
    }
  };

  const Row = ({ index, style }: ListChildComponentProps) => (
    <div
      role="presentation"
      onClick={() => selectOnClick && onSelect(index)}
      key={String(index)}
      style={style}
      className={concat(
        styles.listItem,
        index === selectedIndex && styles.selected,
        selectOnClick && 'cursor-pointer'
      )}
    >
      {items[index]}
    </div>
  );

  const isRowLoaded = (index: number) => Boolean(items[index]);

  const loadItems = (from: number, to: number) => {
    loadMore(from, to);
    return null;
  };

  useEffect(() => {
    window.addEventListener('keydown', keyHandler);
    return () => {
      window.removeEventListener('keydown', keyHandler);
    };
  }, [keyHandler]);

  const listPaddingTop = 11;

  return (
    <LoaderWrapper loading={loading || false} transparent>
      <div className={styles.list}>
        {!loading && total === 0
          ? <NoDataPlaceholder />
          : (
            <AutoSizer className={styles.listBlock}>
              {({ height, width }) => (
                <InfiniteLoader
                  isItemLoaded={isRowLoaded}
                  itemCount={total}
                  loadMoreItems={loadItems}
                  minimumBatchSize={20}
                >
                  {({ onItemsRendered, ref: setListRef }) => (
                    <List
                      height={(footer ? height - footerHeight : height) - listPaddingTop}
                      itemCount={total}
                      itemSize={getItemHeight}
                      width={width}
                      onItemsRendered={onItemsRendered}
                      ref={(ref: List) => {
                        listRef = ref;
                        (setListRef as IBindingCallback1<List>)(ref);
                      }}
                    >
                      {Row}
                    </List>
                  )}
                </InfiniteLoader>
              )}
            </AutoSizer>
          )}
        <div
          className={styles.footer}
          style={{ height: footerHeight }}
        >
          {footer}
        </div>
      </div>
    </LoaderWrapper>
  );
};

export default KeyNavigationList;
